/** @format */
import React, {
	useRef,
	useState,
	useCallback,
	useContext,
	useEffect,
	Suspense,
	lazy
} from 'react';
import StateContext from '../../StateContext';
import DispatchContext from '../../DispatchContext';
import axios from 'axios';
import {
	Map,
	Source,
	Layer,
	NavigationControl,
	ScaleControl,
} from 'react-map-gl';
import mapboxgl from "mapbox-gl"; // This is a dependency of react-map-gl even if you didn't explicitly install it


// eslint-disable-next-line import/no-webpack-loader-syntax
import {
	clusterLayer,
	clusterCountLayer,
	unclusteredPointLayer,
} from '../layers';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../popup/custom-popup.css';
import type { LayerProps, MapRef, GeoJSONSource } from 'react-map-gl';
import StationPopup from '../popup/StationPopup';
import useWindowDimensions from '../Utils/useWindowDimensions.js';
import { BASE_URL, COLLECTION_NAME, sColors, TILE_URL, IMAGE_URL } from '../Utils/constants';
import MobilePopup from '../popup/MobilePopup';
import SearchMobile from '../Pages/SearchMobile';
import LoadingBackdrop from './LoadingBackdrop';
import { getStationData, getDrainageArea, processData } from '../API/apiHelpers';


const PanelModals = lazy(() => import('../Modal/PanelModals'));
const ModalChart = lazy(() => import('../Modal/ModalChart'));
const DialogBox = lazy(() => import('../Modal/DialogBox'));


interface PopupInfo {
	lngLat: [number, number];
	name: string;
	code?: string;
	river?: string;
	basin?: string;
	anomalia?: string;
	sat?: string;
	change?: string;
	level?: string;
	sdate?: string;
	edate?: string;
}

interface Station {
	name: string;
	last_update: string;
	start_date: string;
	basin: string;
	anomaly: number;
	value: number;
	change: number;
	river: string;
	lat: number;
	lon: number;
	sat: string;
}

/* eslint-disable @typescript-eslint/no-var-requires */
// eslint-disable-next-line import/no-webpack-loader-syntax
(mapboxgl as any).workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

// Raster layer configuration
const rasterLayer = {
	id: "satellite-raster",
	type: "raster",
	source: "satellite",
	paint: {
		"raster-opacity": 0.8 // Default opacity
	}
};

function MapComponent() {
	const appDispatch = useContext(DispatchContext);
	const appState = useContext(StateContext);
	const { height, width } = useWindowDimensions();

	const [rasterOpacity, setRasterOpacity] = React.useState(0.8);


	//const API_KEY = process.env.REACT_APP_API_KEY;
	const API_KEY = 'pk.eyJ1IjoiYm1tb3JlaXJhIiwiYSI6ImNsMG16MmxvdTE3ZzEzZXB3aWZqNnd6azIifQ.ZOe3fv_Xwbr8TIm5L007yQ';

	const iniZoom: number = 4;
	const iniLat = -13.91;
	const iniLon = -58.59;

	const unclustered: LayerProps = {
		id: 'unclustered-symbol',
		type: 'symbol',
		source: 'vs_All',
		filter: ['!', ['has', 'point_count']],
		paint: {
			'text-color': [
				'step',
				['get', appState.ucField],
				'#ffffff',
				-1.5,
				'#ffffff',
				-1.0,
				'#000000',
				-0.5,
				'#000000',
				0,
				'#000000',
				0.5,
				'#000000',
				1.0,
				'#ffffff',
				1.5,
				'#ffffff',
			],
		},
	};

	if (API_KEY == null) {
		throw new Error(
			'You have to configure env REACT_APP_API_KEY, see README'
		);
	}

	const [chartModal, setChartModal] = useState(false);
	const toggleChartModal = () => {
		// change search value to blank to fix next search with same value useEffect
		appDispatch({
			type: 'searchAction',
			searchEventValue: '',
		});
		if (width > 900) {
			setChartModal((prevState) => !prevState);
		} else {
			appDispatch({
				type: 'openDialogBox',
				value: 'chart',
			});
		}

	}
	//close popup function
	function popupNull() {
		setPopupInfo(null);
		appDispatch({
			type: 'closePopup',
		});
		/* let resizable = document.getElementById('map-wrap');
		resizable.style.height = '100vh';
		mapRef.current.easeTo({
			center: [iniLon, iniLat],
			zoom: iniZoom,
			duration: 1500,
			pitch: 0, // pitch in degrees
			bearing: 0, // bearing in degrees
		}); */
	}

	const mapRef = useRef<MapRef>(null);

	const [cursor, setCursor] = useState<string>('auto');
	const [popupInfo, setPopupInfo] = useState<PopupInfo | null>(null);



	const onClick = (event) => {
		// If we let the click event propagates to the map, it will immediately close the popup
		// with `closeOnClick: true`
		event.originalEvent.stopPropagation();
		//window.scrollTo(0, 150);
		const feature = event.features[0];

		if (feature) {
			if (feature.properties.name) {
				const nameSplit = feature.properties.name.split('_');

				/* mapRef.current.easeTo({
					center: feature.geometry.coordinates,

					pitch: 120, // pitch in degrees
					bearing: 60, // bearing in degrees
					zoom: 8,
					duration: 1500,
				}); */

				appDispatch({
					type: 'stationArea',
					valueSource: feature.properties.name,
				});

				appDispatch({
					type: 'loadStation',
					stValue: {
						lngLat: feature.geometry.coordinates,
						name: feature.properties.name,
						river: feature.properties.river,
						basin: feature.properties.basin,
						anomalia: feature.properties.anomalia,
						change: feature.properties.change,
						level: feature.properties.value,
						sdate: feature.properties.s_date,
						edate: feature.properties.e_date,
						code: nameSplit[3],
						id: feature.properties.id,
					},
				});

				setPopupInfo({
					lngLat: feature.geometry.coordinates,
					name: feature.properties.name,
					river: feature.properties.river,
					basin: feature.properties.basin,
					anomalia: feature.properties.anomalia,
					sat: feature.properties.sat,
					change: feature.properties.change,
					level: feature.properties.value,
					sdate: feature.properties.s_date,
					edate: feature.properties.e_date,
					code: nameSplit[3],
				});
				//console.log(feature.properties.name);
				//let mapElement = document.getElementById('map-wrap');
				//let newHeight = Number(window.innerHeight) - 90;
				//mapElement.style.height = `${newHeight}px`;
				//mapRef.current.resize();
			} else {
				const clusterId = feature.properties.cluster_id;
				// resolvendo bug do zoom com erro do id
				const mapboxSource = mapRef.current.getSource(
					'All'
				) as GeoJSONSource;

				mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
					if (err) {
						return;
					}

					mapRef.current.easeTo({
						center: feature.geometry.coordinates,
						zoom,
						duration: 500,
					});
				});
			}
		}
	};

	const onMouseEnter = useCallback(() => setCursor('pointer'), []);
	const onMouseLeave = useCallback(() => setCursor('auto'), []);

	async function getStationData(id: string) {
		try {

			const response = await axios
				.get(`${BASE_URL}/${COLLECTION_NAME}?filters[name]=${id}`)
				.then((response) => {
					const entries = response.data.data;
					console.log(entries);
					console.log('entries: ' + entries.length);
					if (entries.length > 0) {
						//const dataset = processData(entries[0]); 7/5/2025
						const dataset = processData(entries[0].attributes);
						console.log('process data ' + dataset.yearEnd
						);
						appDispatch({
							type: 'loadDataFromDB',
							valueOverall: dataset.overall,
							valueSelection: dataset.selection,
							valueMin: dataset.yearMin,
							valueMax: dataset.yearMax,
							valueStart: dataset.yearStart,
							valueEnd: dataset.yearEnd,
						});
						toggleChartModal();

						console.log(
							'%c EVENT: click button getData - toggleModalChat! ',
							'background: #222; color: #bada55'
						);
					} else {
						console.log(
							'No entry found with the specified custom specific field.!!!'
						);
					}
				})
				.catch((err) => {
					console.log(err);
					console.log(
						'No entry found with the specified custom specific field.!!!'
					);
				});
		} catch (error: any) {
			console.log(error);
		}
	}



	async function getDrainageArea(id: string, appDispatch: React.Dispatch<any>) {
		try {

			const response = await axios
				.get(`${BASE_URL}/${COLLECTION_NAME}?filters[name]=${id}`)
				.then((response) => {
					const entries = response.data.data;
					console.log(entries);
					if (entries.length > 0) {
						appDispatch({
							type: 'drainageArea',
							valueArea: entries[0].attributes.drainage,
						});

						console.log(
							'%c EVENT: click button getDrainageArea! ',
							'background: #222; color: #bada55'
						);
					} else {
						console.log(
							'No entry found with the specified custom specific field.!!!'
						);
					}
				})
				.catch((err) => {
					console.log(err);
					console.log(
						'No entry found with the specified custom specific field.!!!'
					);
				});
		} catch (error: any) {
			console.log(error);
		}
	}

	function processData(dataset) {
		console.log('process data ' + dataset);
		const dataSelection = [];
		let max = dataset.overall.maxData;
		let min = max.concat(dataset.overall.minData);
		let men = min.concat(dataset.overall.men);
		let cur = men.concat(dataset.overall.curData);
		let overall = cur;
		overall.sort(function (a, b) {
			return a.dateTime - b.dateTime;
		});

		dataset.data._dataSet.forEach((value) => {
			let dateObject = new Date(value.date);
			let month = dateObject.getUTCMonth() + 1; //months from 1-12
			let day = dateObject.getUTCDate();
			let year = dateObject.getUTCFullYear();

			dataSelection.push({
				height: Number(value.height),
				uncertainty: Number(value.uncertainty),
				hv: Number(value.height) + Math.abs(value.uncertainty),
				lv: Number(value.height) - Math.abs(value.uncertainty),
				date: year + '-' + month + '-' + day,
				dateString: value.date,
			});
		});

		const data = {
			isSet: true,
			overall: overall,
			selection: dataSelection,
			dataCur: dataset.overall.curData,
			yearMin: dataset.overall.minYear,
			yearMax: dataset.overall.maxYear,
			yearStart: dataset.start,
			yearEnd: dataset.last,
			name: dataset.name,
			latitude: dataset.latitude,
			longitude: dataset.longitude,
			lastUpdate: dataset.updatedAt,
		};
		return data;
	}



	const flyToStation = (coord: number[]) => {
		// blank search value to avoid bug on next search
		appDispatch({
			type: 'searchAction',
			searchEventValue: '',
		});
		mapRef.current.flyTo({
			center: [coord[0], coord[1]], // Fly to the selected target
			duration: 6000, // Animate over 6 seconds
			essential: true, // This animation is considered essential with
			zoom: 10,
			//respect to prefers-reduced-motion
		});
	};

	/* async function search(type: string, value: string) {
		const sat = appState.selectedSat.slice(3);

		console.log(appState.selectedSat);

		if (appState.selectedSat === '' || appState.selectedSat === 'vs_All') {
			try {
				const res = await axios(
					`${BASE_URL}/${COLLECTION_NAME}?filters[${type}][$contains]=${value.toUpperCase()}`
				);
				const stationData = await res.data.data;
				if (stationData.length > 0) {
					appDispatch({
						type: 'searchDataAction',
						searchDataValue: stationData,
					});
					console.log(stationData);
				}
			} catch (error) {
				console.log(error);
			}
		} else {
			try {
				const res = await axios(
					`${BASE_URL}/${COLLECTION_NAME}?filters[sat][$contains]=${sat}&filters[${type}][$contains]=${value.toUpperCase()}`
				);
				const stationData = await res.data.data;
				if (stationData.length > 0) {
					appDispatch({
						type: 'searchDataAction',
						searchDataValue: stationData,
					});
					console.log(stationData);
				}
			} catch (error) {
				console.log(error);
			}
		}
	} */

	const _onIdle = () => {
		//console.log(mapRef.current);

	};

	useEffect(() => {
		if (appState.drainageAreaName) {
			getDrainageArea(appState.drainageAreaName, appDispatch);
		}
	}, [appState.drainageAreaName]);

	useEffect(() => {
		loadFeatures();
	}, []);

	async function loadFeatures() {
		try {
			const res = await axios({
				method: 'get',
				url: `https://hydrologyfromspace.org/assets/data/geojson/vs_All.geojson`,
				headers: {
					'Access-Control-Allow-Origin': false,
				},
			});

			if (res.data) {
				appDispatch({
					type: 'filterFeatures',
					value: res.data,
				});
				appDispatch({
					type: 'stationFeatures',
					value: res.data,
				});
				const list = createStationList(
					res.data.features
				);
				const lastUpdated = filterUpdatedLastStations(
					list,
					30
				);
				//console.log(res.data.features);
				appDispatch({
					type: 'setLastUpdated',
					value: lastUpdated,
				});
			}
		} catch (error) {
			console.log(error);
		} finally {
			setTimeout(() => {
				appDispatch({
					type: 'toggleBackdrop',
					value: false,
				});
				console.log('map engine onIdle');
			}, 1000);
		}
	}

	const baciasFills2: LayerProps = {
		id: 'bacias2-fills',
		type: 'fill',
		source: 'bacias2',
		layout: {
			// Make the layer visible by default.
			visibility: 'visible',
		},
		paint: {
			'fill-color': '#627BC1',
			'fill-opacity': [
				'case',
				['boolean', ['feature-state', 'hover'], false],
				0.7,
				0.3,
			],
		},
	};

	const baciasBorders2: LayerProps = {
		id: 'bacias2-borders',
		type: 'line',
		source: 'bacias2',
		layout: {
			// Make the layer visible by default.
			visibility: 'visible',
		},
		paint: {
			'line-color': '#627BC1',
			'line-width': 2,
		},
	};

	const createStationList = (features) => {
		const newArray: Station[] = features.map((item) => {
			return {
				'name': item.properties.name,
				'last_update': item.properties.e_date,
				'start_date': item.properties.s_date,
				'basin': item.properties.basin,
				'anomaly': item.properties.anomalia,
				'value': item.properties.value,
				'change': item.properties.change,
				'river': item.properties.river,
				'lat': item.properties.lat,
				'lon': item.properties.long,
				'sat': item.properties.sat,
			};
		});

		return newArray;
	};

	const filterUpdatedLastStations = (
		stationList: Station[],
		days: number
	): Station[] => {
		const result: Station[] = stationList.filter((station) => {
			const dateString: string = station.last_update;
			const dateFormatted: Date = new Date(dateString);
			const dateNow: Date = new Date();
			const differenceMiliseconds: number =
				Number(dateNow) - Number(dateFormatted);
			const differenceinDays: number =
				differenceMiliseconds / (1000 * 60 * 60 * 24);
			return differenceinDays <= days;
		});
		result.sort((a, b) => {
			const dateA = new Date(a.last_update);
			const dateB = new Date(b.last_update);
			return Number(dateB) - Number(dateA);
		});
		return result;
	};

	const onMapLoad = useCallback(() => {
		const map = mapRef.current.getMap()
		//turn off option to rotate map with SHIFT+Cursor
		map.keyboard.disableRotation()
		map.touchZoomRotate.disableRotation()
	}, []);

	const moveLayerToTop = useCallback(() => {
		const map = mapRef.current?.getMap();
		if (map && map.getLayer('clusters')) {
			try {
				// Move the clusters layer to be the topmost layer
				map.moveLayer('clusters');
				map.moveLayer('unclustered-point');
				map.moveLayer('unclustered-symbol');
				
				// Also move the cluster count layer if it exists
				if (map.getLayer('cluster-count')) {
					map.moveLayer('cluster-count');
				}
			} catch (error) {
				console.log('Error moving layers:', error);
			}
		}
	}, []);

	// Call this in useEffect or onLoad
	useEffect(() => {
		moveLayerToTop();
	}, [moveLayerToTop]);

	// Call this when satellite layer toggles
	useEffect(() => {
		if (appState.satLayer) {
			// Small delay to ensure satellite layer is loaded
			setTimeout(() => {
				moveLayerToTop();
			}, 100);
		}
	}, [appState.satLayer, moveLayerToTop]);

	// Call this when selectedLayer changes
useEffect(() => {
    if (appState.selectedLayer) {
        // Small delay to ensure layer is loaded
        setTimeout(() => {
            moveLayerToTop();
        }, 100);
    }
}, [appState.selectedLayer, moveLayerToTop]);

	return (
		<>
			<div id="map-wrap" className="map-wrap">
				<Map
					initialViewState={{
						latitude: iniLat,
						longitude: iniLon,
						zoom: iniZoom,
					}}
					mapStyle={'mapbox://styles/mapbox/dark-v9'}
					mapboxAccessToken={API_KEY}
					interactiveLayerIds={[clusterLayer.id, unclusteredPointLayer.id]}
					onClick={onClick}
					onMouseEnter={onMouseEnter}
					onMouseLeave={onMouseLeave}
					onIdle={_onIdle}
					touchZoomRotate={true}
					touchPitch={false}
					dragRotate={false}
					ref={mapRef}
				>
					<SearchMobile />
										{appState.satLayer && (
						<Source
							id="satellite"
							type="raster"
							tiles={[
								`${TILE_URL}/tiles/WebMercatorQuad/{z}/{x}/{y}.png?url=${IMAGE_URL}/${appState.selectedLayer}&rescale=${appState.rescaleMin},${appState.rescaleMax}&nodata=0&colormap_name=${appState.colormap}`

							]}
							tileSize={256}
							bounds={appState.activeLayerBounds}
							minzoom={3}
							maxzoom={20}
							key={`${appState.rescaleMin}-${appState.rescaleMax}-${appState.colormap}-${appState.selectedLayer}`} // Force re-render when layer changes
						>
							<Layer {...({ ...rasterLayer, paint: { "raster-opacity": appState.rasterOpacity } } as LayerProps)} />
						</Source>
					)}
					{appState.drainageArea ? (
						<Source
							id="bacias"
							type="geojson"
							data={appState.drainageData}
						>
							<Layer {...baciasFills2} />
							<Layer {...baciasBorders2} />
						</Source>
					) : (
						<Source
							id="bacias"
							type="geojson"
							data={`/assets/data/geojson/layer_dummy.geojson`}
						>
							<Layer {...baciasFills2} />
							<Layer {...baciasBorders2} />
						</Source>
					)}

					{appState.allSat && (
						<Source
							id="All"
							type="geojson"
							data={appState.filteredFeatures}
							cluster={true}
							clusterMaxZoom={appState.clusterMaxZoom}
							clusterRadius={appState.clusterRadius}
							clusterMinPoints={10}
						>
							<Layer
								id={'clusters'}
								type={'circle'}
								source={'All'}
								filter={['has', 'point_count']}
								paint={{
									'circle-color': [
										'step',
										['get', 'point_count'],
										'#d1d6d7',
										100,
										'#9fa4a5',
										750,
										'#595c5d',
									],
									'circle-radius': [
										'step',
										['get', 'point_count'],
										20,
										100,
										30,
										750,
										40,
									],
								}}
							/>
							<Layer {...clusterCountLayer} />
							<Layer
								id={'unclustered-point'}
								type={'circle'}
								source={'All'}
								filter={['!', ['has', 'point_count']]}
								paint={{
									'circle-color': [
										'step',
										['get', appState.ucField],
										sColors.red,
										-1.5,
										sColors.orange,
										-1.0,
										sColors.orangeLight,
										-0.5,
										sColors.yellow,
										0,
										sColors.blueLight,
										0.5,
										sColors.blue,
										1.0,
										sColors.blueDark,
										1.5,
										sColors.blueExtra,
									],
									'circle-radius': 13,
									'circle-stroke-width': [
										'step',
										['get', appState.ucField],
										2,
										-1.5,
										2,
										-1.0,
										2,
										-0.5,
										2,
										0,
										2,
										0.5,
										2,
										1.0,
										2,
										1.5,
										2,
									],
									'circle-stroke-color': '#fff',
								}}
							/>
							<Layer
								layout={{
									'text-field': ['get', appState.ucField],
									'text-font': [
										'DIN Offc Pro Medium',
										'Arial Unicode MS Bold',
									],
									'text-size': 10,
								}}
								{...unclustered}
							/>
						</Source>
					)}

					<NavigationControl position="top-left" />
					<ScaleControl />
					{popupInfo && (
						width < 900 ? (
							<MobilePopup
								sObj={popupInfo}
								getData={getStationData}
								closePopup={popupNull}
							/>
						) : (
							<StationPopup
								sObj={popupInfo}
								getData={getStationData}
								closePopup={popupNull}
							/>
						)
					)}
					<Suspense fallback={<><LoadingBackdrop open={true} /></>}>
						<PanelModals flyTo={flyToStation} />
					</Suspense>

					{chartModal && (
						<Suspense fallback={<><LoadingBackdrop open={true} /></>}>
							<ModalChart
								show={chartModal}
								onHide={() => setChartModal(false)}
							/></Suspense>
					)}


				</Map>
				<Suspense fallback={<><LoadingBackdrop open={true} /></>}>
					<DialogBox flyTo={flyToStation} />
				</Suspense>
				<LoadingBackdrop open={appState.backdrop} />


			</div>
		</>
	);
}

export default MapComponent;
